import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Save, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useServiceLines } from "../lib/serviceLines";

const SERVICE_LINES = ["GHA", "GHBC", "GLH", "TVA", "FH", "GHI"];

// Fallback labels (used until /api/service-lines responds). Dynamic name
// from the backend overrides these — that's where Forest Hills DC comes from.
const LABELS = {
  GHA: "Goodwin House Alexandria",
  GHBC: "Goodwin House Bailey's Crossroads",
  GLH: "Goodwin Living at Home",
  TVA: "The View Alexandria",
  FH: "Forest Hills DC",
  GHI: "Home Office",
};

export default function HeadcountSettings() {
  const { user } = useAuth();
  const { nameOf } = useServiceLines();
  const [headcount, setHeadcount] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Phase 4b SLA timers
  const [slaStaleDays, setSlaStaleDays] = useState(14);
  const [slaEscalateDays, setSlaEscalateDays] = useState(30);
  // Phase 5 cadence
  const [cadenceHigh, setCadenceHigh] = useState(3);
  const [cadenceEsc, setCadenceEsc] = useState(7);
  const [cadenceStd, setCadenceStd] = useState(14);
  const [savingSla, setSavingSla] = useState(false);
  const canEdit = user?.role === "hr";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/headcount");
      setHeadcount(data.headcount || {});
      setUpdatedAt(data.updated_at);
      if (typeof data.sla_stale_days === "number") setSlaStaleDays(data.sla_stale_days);
      if (typeof data.sla_escalate_days === "number") setSlaEscalateDays(data.sla_escalate_days);
      if (typeof data.cadence_high_risk_days === "number") setCadenceHigh(data.cadence_high_risk_days);
      if (typeof data.cadence_escalated_days === "number") setCadenceEsc(data.cadence_escalated_days);
      if (typeof data.cadence_standard_days === "number") setCadenceStd(data.cadence_standard_days);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const total = SERVICE_LINES.reduce((acc, k) => acc + (parseInt(headcount[k], 10) || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { headcount: {} };
      for (const k of SERVICE_LINES) {
        const v = parseInt(headcount[k], 10);
        if (Number.isNaN(v) || v < 0) {
          toast.error(`${k} must be a non-negative integer`);
          setSaving(false);
          return;
        }
        body.headcount[k] = v;
      }
      const { data } = await api.put("/settings/headcount", body);
      setUpdatedAt(data.updated_at);
      toast.success("Headcount saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSla = async () => {
    setSavingSla(true);
    try {
      const stale = parseInt(slaStaleDays, 10);
      const escalate = parseInt(slaEscalateDays, 10);
      if (Number.isNaN(stale) || stale < 1) {
        toast.error("Stale days must be a positive integer");
        return;
      }
      if (Number.isNaN(escalate) || escalate < 1) {
        toast.error("Escalate days must be a positive integer");
        return;
      }
      if (stale >= escalate) {
        toast.error("Stale days must be strictly less than escalate days");
        return;
      }
      const { data } = await api.put("/settings/sla", {
        sla_stale_days: stale,
        sla_escalate_days: escalate,
        cadence_high_risk_days: parseInt(cadenceHigh, 10),
        cadence_escalated_days: parseInt(cadenceEsc, 10),
        cadence_standard_days: parseInt(cadenceStd, 10),
      });
      setSlaStaleDays(data.sla_stale_days);
      setSlaEscalateDays(data.sla_escalate_days);
      setCadenceHigh(data.cadence_high_risk_days);
      setCadenceEsc(data.cadence_escalated_days);
      setCadenceStd(data.cadence_standard_days);
      toast.success("SLA + cadence timers saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingSla(false);
    }
  };

  return (
    <div data-testid="headcount-settings-page" className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--page-fg)]">Headcount</h1>
        <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
          Per–service line employee counts. Used in Phase 2 dashboards to compute case rates.
        </p>
      </div>

      <div className="brand-card p-6">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-teal text-white">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[color:var(--page-fg)]">Total headcount</p>
              <p className="text-xs text-[color:var(--page-fg-muted)]">
                {loading ? "Loading…" : `${total.toLocaleString()} employees across ${SERVICE_LINES.length} service lines`}
              </p>
            </div>
          </div>
          {updatedAt && (
            <p className="text-xs text-[color:var(--page-fg-muted)]">
              Updated {new Date(updatedAt).toLocaleString()}
            </p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {SERVICE_LINES.map((sl) => (
            <div
              key={sl}
              className="flex items-center gap-3 rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
              data-testid={`headcount-row-${sl}`}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color:var(--surface-2)] text-xs font-semibold text-brand-teal">
                {sl}
              </div>
              <div className="flex-1">
                <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                  {nameOf(sl) !== sl ? nameOf(sl) : LABELS[sl]}
                </Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={headcount[sl] ?? ""}
                  onChange={(e) =>
                    setHeadcount((h) => ({ ...h, [sl]: e.target.value }))
                  }
                  disabled={!canEdit}
                  data-testid={`headcount-input-${sl}`}
                  className="mt-1 h-9 rounded-lg bg-white"
                />
              </div>
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="mt-6 flex items-center justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || loading}
              data-testid="headcount-save-button"
              className="bg-brand-teal text-white hover:bg-brand-tealDark"
            >
              <Save className="mr-1 h-4 w-4" />
              {saving ? "Saving…" : "Save headcount"}
            </Button>
          </div>
        )}
        {!canEdit && (
          <p className="mt-4 rounded-lg bg-[color:var(--surface-2)] p-3 text-xs text-[color:var(--page-fg-muted)]">
            Leadership has read-only access. HR can edit headcount values.
          </p>
        )}
      </div>

      {/* Phase 4b: SLA Settings card */}
      <div className="brand-card p-6" data-testid="sla-settings-card">
        <div className="mb-5 flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl text-brand-ivory"
            style={{ backgroundColor: "#F76808" }}
          >
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[color:var(--page-fg)]">SLA timers</p>
            <p className="text-xs text-[color:var(--page-fg-muted)]">
              Drives <span className="font-medium">stale_case</span> notifications and
              automatic <span className="font-medium">Escalated</span> status.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
            <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
              Stale threshold (days)
            </Label>
            <Input
              type="number"
              min={1}
              max={365}
              step={1}
              value={slaStaleDays}
              onChange={(e) => setSlaStaleDays(e.target.value)}
              disabled={!canEdit || loading}
              data-testid="sla-stale-days-input"
              className="mt-1 h-9 rounded-lg bg-white"
            />
            <p className="mt-1 text-[11px] text-[color:var(--page-fg-muted)]">
              No audit activity for N days → notify assigned HR partner.
            </p>
          </div>
          <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
            <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
              Escalate threshold (days)
            </Label>
            <Input
              type="number"
              min={1}
              max={365}
              step={1}
              value={slaEscalateDays}
              onChange={(e) => setSlaEscalateDays(e.target.value)}
              disabled={!canEdit || loading}
              data-testid="sla-escalate-days-input"
              className="mt-1 h-9 rounded-lg bg-white"
            />
            <p className="mt-1 text-[11px] text-[color:var(--page-fg-muted)]">
              Open longer than N days → auto-set status to <b>Escalated</b> + notify Leadership.
            </p>
          </div>
        </div>

        {/* Phase 5 — cadence reminders */}
        <div className="mt-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)]">
            Cadence reminders (days since last activity)
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">High-risk</Label>
              <Input
                type="number"
                min={1}
                max={365}
                step={1}
                value={cadenceHigh}
                onChange={(e) => setCadenceHigh(e.target.value)}
                disabled={!canEdit || loading}
                data-testid="cadence-high-risk-input"
                className="mt-1 h-9 rounded-lg bg-white"
              />
            </div>
            <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Escalated / pending</Label>
              <Input
                type="number"
                min={1}
                max={365}
                step={1}
                value={cadenceEsc}
                onChange={(e) => setCadenceEsc(e.target.value)}
                disabled={!canEdit || loading}
                data-testid="cadence-escalated-input"
                className="mt-1 h-9 rounded-lg bg-white"
              />
            </div>
            <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
              <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Standard</Label>
              <Input
                type="number"
                min={1}
                max={365}
                step={1}
                value={cadenceStd}
                onChange={(e) => setCadenceStd(e.target.value)}
                disabled={!canEdit || loading}
                data-testid="cadence-standard-input"
                className="mt-1 h-9 rounded-lg bg-white"
              />
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="mt-5 flex items-center justify-end">
            <Button
              onClick={handleSaveSla}
              disabled={savingSla || loading}
              data-testid="sla-save-button"
              className="bg-brand-teal text-white hover:bg-brand-tealDark"
            >
              <Save className="mr-1 h-4 w-4" />
              {savingSla ? "Saving…" : "Save SLA timers"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
